import { useMemo } from 'react'
import { Lightbulb, MonitorPlay, Volume2 } from 'lucide-react'
import { Shell, useShellMeta } from '../../../admin/Shell.js'
import { fmtNombre, fmtRatio, nomSalle, renommerSalles, Pastille, PastilleEtat, TON_ALIM, useVueSonde } from '../commun.js'
import {
  Bandeaux, donneeDe, EnTeteDomaine, Journal, LienAppareil, Lu, Panneau, Sources, Tuile, Tuiles, useEvenements, usePeutDetails, Vide, Zone,
} from '../video/kit.js'
import { agePlusAncien, fmtDecimal, lienSalle, type SalleDomaine, type SourceDomaine } from '../video/kit-modele.js'
import { Link } from '../../../router.js'
import {
  alertesSon, estLightingDoc, estSoundDoc, etatPort, jaugeSon, mesurePerimee, mesuresReseauLumiere, origineLimite, portsEnMaintien, tonSonometre,
  type LightingDoc, type NoeudLumiere, type Sonometre, type SoundDoc,
} from './av-modele.js'
import { useMenuSalle } from '../menu-salle.js'
import './audiovisuel.css'

/**
 * TECHNIQUE › AUDIOVISUEL (21/10/2026) — `GET /v1/lighting` et `GET /v1/sound`.
 *
 * Les salles en tête (courant, son, lumière), puis le son — jauge LAeq
 * 15 min projetée et message de Sonde, conséquence d'abord — et la lumière
 * — nœuds LumiNode, 12 ports DMX, univers, MA-Net. Un DMX Hold est un
 * maintien, jamais un plantage. Lecture seule, aucun conseil.
 */

const KINDS = [
  'SOUND_LIMIT_EXCEEDED', 'SOUND_LIMIT_FORECAST', 'LAEQ_WARN', 'SOUND_METER_SILENT', 'SOUND_METER_RECOVERED',
  'DMX_HOLD', 'DMX_RECOVERED', 'DMX_NODE_DOWN', 'DMX_NODE_UP', 'DMX_NODE_RESTART', 'DMX_PORT_IDLE', 'DMX_PORT_STOPPED',
  'DMX_UNIVERSE_SILENT', 'DMX_UNIVERSE_BACK', 'DMX_GAPS_REPEATED', 'MANET_RATE', 'LUMINODE_WS_LOST', 'LUMINODE_UNREADABLE',
]

export function AudiovisuelPage() {
  const meta = useShellMeta()
  const details = usePeutDetails()
  const lum = useVueSonde('lighting', estLightingDoc)
  const son = useVueSonde('sound', estSoundDoc)
  const L = donneeDe(lum.lecture, lum.precedente)
  const S = donneeDe(son.lecture, son.precedente)
  const ev = useEvenements(KINDS, 25)

  const sources = useMemo(() => {
    const m = new Map<string, SourceDomaine>()
    for (const s of [...(S.d?.sources ?? []), ...(L.d?.sources ?? [])]) if (!m.has(s.code)) m.set(s.code, s)
    return [...m.values()]
  }, [L.d, S.d])
  const alertes = useMemo(() => (S.d ? alertesSon(S.d.meters) : []), [S.d])
  const memeMessage = lum.lecture && son.lecture && lum.lecture.etat !== 'ok' && son.lecture.etat !== 'ok' && lum.lecture.message === son.lecture.message

  return (
    <Shell active="technique" meta={meta}>
      <main className="lc-page lc-page--wide lc-stagger sv-dom-page sv-av">
        <EnTeteDomaine
          retour={{ href: '/technique', label: 'Technique' }}
          icone={<MonitorPlay size={26} />}
          titre="Audiovisuel"
          sousTitre="Le son et la lumière des salles : niveau sonore face à la limite, ports DMX, univers et réseau lumière. Lu sur Sonde, sans rien modifier."
          genereA={L.d?.generated_at ?? S.d?.generated_at}
          perime={L.perime || S.perime}
        />
        <Bandeaux lecture={lum.lecture} genereA={L.d?.generated_at} collector={L.d?.collector} />
        {memeMessage ? null : <Bandeaux lecture={son.lecture} genereA={S.d?.generated_at} collector={L.d ? null : S.d?.collector} />}

        {alertes.length ? (
          <ul className="sv-av-alertes" aria-label="Son : à surveiller">
            {alertes.map((a) => (
              <li key={a.uid} className={`sv-av-alerte sv-av-alerte--${a.ton}`}><Volume2 size={18} aria-hidden="true" /> <span>{a.texte}</span></li>
            ))}
          </ul>
        ) : null}

        {L.d ? <Salles zones={L.d.zones} lumiere={L.d} son={S.d} perime={L.perime} /> : null}

        <div className="sv-dom-colonnes">
          <Panneau id="sv-av-son" titre="Son" indice={S.d?.horizon_min != null ? `LAeq 15 min, projection à ${S.d.horizon_min} min` : 'LAeq 15 min'}>
            {S.d ? <Zone perime={S.perime}><Son d={S.d} /></Zone> : <p className="lc-hint">{son.lecture && son.lecture.etat !== 'ok' ? son.lecture.message : 'Lecture en cours…'}</p>}
          </Panneau>
          <Panneau id="sv-av-lumiere" titre="Lumière" indice="nœuds LumiNode, ports DMX, univers">
            {L.d ? <Zone perime={L.perime}><Lumiere d={L.d} details={details} /></Zone> : <p className="lc-hint">{lum.lecture && lum.lecture.etat !== 'ok' ? lum.lecture.message : 'Lecture en cours…'}</p>}
          </Panneau>
        </div>

        <div className="sv-dom-colonnes">
          <Panneau id="sv-av-journal" titre="Journal son et lumière" indice="dernières 24 h, message de Sonde">
            <Journal lecture={ev.lecture} precedente={ev.precedente} vide="Aucun événement son ou lumière dans les dernières 24 heures." />
          </Panneau>
          <Panneau id="sv-av-sources" titre="Sources" indice="d’où viennent ces chiffres">
            <Sources sources={sources} details={details} />
          </Panneau>
        </div>
      </main>
    </Shell>
  )
}

/* ─────────────────────────────── Salles */

function Salles({ zones, lumiere, son, perime }: { zones: SalleDomaine[]; lumiere: LightingDoc; son: SoundDoc | null; perime: boolean }) {
  const menuSalle = useMenuSalle()
  if (zones.length === 0) return null
  return (
    <Zone perime={perime}>
      <ul className="sv-av-salles" aria-label="Salles">
        {zones.map((z) => {
          const noeuds = lumiere.nodes.filter((n) => n.device.zone_code === z.code)
          const ports = noeuds.flatMap((n) => n.ports)
          const actifs = ports.filter((p) => etatPort(p).ton === 'ok').length
          const maintien = ports.filter((p) => etatPort(p).ton === 'warn').length
          const metre = son?.meters.find((m) => m.device.zone_code === z.code) ?? null
          const ton = TON_ALIM[z.power.situation] ?? 'gris'
          return (
            <li key={z.code} className={`sv-av-salle sv-av-salle--${ton}`}>
              <Link className="sv-av-salle__lien" href={lienSalle(z.code)} {...menuSalle.salle({ code: z.code, label: z.label })}>
                <span className="sv-av-salle__nom">{nomSalle(z.code, z.label)}</span>
                <Pastille ton={ton}>{z.power.label_fr}</Pastille>
                <span className="sv-av-salle__ligne"><Volume2 size={14} aria-hidden="true" />
                  {metre ? <>{fmtDecimal(metre.laeq15_db, 'dB(A)')} <span className="lc-hint">sur 15 min{metre.limit_a_db != null ? ` · limite ${fmtDecimal(metre.limit_a_db, 'dB')}` : ''}</span></> : <span className="lc-hint">pas de sonomètre suivi</span>}
                </span>
                <span className="sv-av-salle__ligne"><Lightbulb size={14} aria-hidden="true" />
                  {ports.length ? <>{actifs}/{ports.length} ports DMX actifs{maintien ? <span className="sv-av-salle__hold"> · {maintien} en maintien</span> : null}</> : <span className="lc-hint">pas de nœud lumière suivi</span>}
                </span>
                <span className="sv-av-salle__meta">{z.operating ? 'En exploitation' : 'Hors exploitation'}{z.power.source === 'unavailable' ? ' · courant non lu' : ''}</span>
              </Link>
            </li>
          )
        })}
      </ul>
    </Zone>
  )
}

/* ─────────────────────────────── Son */

function Son({ d }: { d: SoundDoc }) {
  const source = d.sources.find((s) => s.code === 'sound_meters') ?? null
  if (d.meters.length === 0) {
    return (
      <Vide titre="Aucun sonomètre n’est suivi par Sonde." versADecider>
        <p>
          {source?.last_success_at ? 'La source « Sonomètres » répond, mais aucun sonomètre n’est rattaché au registre.' : 'La source « Sonomètres » n’a encore rien relevé.'}
          {' '}Sans sonomètre, ni jauge ni prévision « limite atteinte dans N min » : rien n’est inventé.
        </p>
        {d.devices.length ? <p>{d.devices.length} appareil{d.devices.length > 1 ? 's' : ''} son au registre, sans mesure LAeq.</p> : null}
      </Vide>
    )
  }
  return (
    <div className="sv-av-metres">
      {d.meters.map((m) => <Metre key={m.device.uid} m={m} />)}
    </div>
  )
}

function Metre({ m }: { m: Sonometre }) {
  const ton = tonSonometre(m)
  const j = jaugeSon(m)
  return (
    <article className={`sv-av-metre sv-av-metre--${ton}`}>
      <header className="sv-av-metre__tete">
        <span className="sv-av-metre__nom"><LienAppareil uid={m.device.uid} nom={m.device.display_name} zone={m.device.zone_code} />{m.device.zone_code ? <span className="lc-hint"> · {nomSalle(m.device.zone_code)}</span> : null}</span>
        {m.level && mesurePerimee(m.data_age_s) ? <Pastille ton="gris">Donnée périmée</Pastille>
          : m.level ? <Pastille ton={ton}>{m.level === 'critical' ? 'Limite atteinte ou toute proche' : m.level === 'warning' ? 'Proche de la limite' : 'Sous la limite'}</Pastille>
          : <PastilleEtat etat={m.device.state.display_state} libelle={m.device.state.display_label} />}
      </header>
      {m.message_fr ? <p className="sv-av-metre__message">{renommerSalles(m.message_fr)}</p> : null}
      <p className="sv-av-metre__valeur">
        <span className="sv-av-metre__chiffre">{fmtDecimal(m.laeq15_db)}</span> <span className="sv-av-metre__unite">dB(A) sur 15 min</span>
      </p>
      {j ? (
        <div className="sv-av-jauge" role="img"
             aria-label={`LAeq 15 min ${fmtDecimal(m.laeq15_db, 'dB')}, projeté ${fmtDecimal(m.proj_laeq15_db, 'dB')}, limite ${fmtDecimal(m.limit_a_db, 'dB')}`}>
          <div className="sv-av-jauge__piste">
            {j.actuel !== null ? <span className="sv-av-jauge__remplie" style={{ width: `${j.actuel}%` }} /> : null}
            {j.projete !== null ? <span className="sv-av-jauge__projete" style={{ left: `${j.projete}%` }} title="Projection" /> : null}
            <span className="sv-av-jauge__limite" style={{ left: `${j.limite}%` }} title="Limite" />
          </div>
          <div className="sv-av-jauge__echelle"><span>{fmtNombre(j.min)} dB</span><span>limite {fmtDecimal(m.limit_a_db)} dB</span></div>
        </div>
      ) : <p className="lc-hint">Limite LAeq inconnue : pas de jauge.</p>}
      <dl className="sv-dom-faits">
        <div><dt>Projeté (15 min)</dt><dd>{fmtDecimal(m.proj_laeq15_db, 'dB(A)')}</dd></div>
        <div><dt>Marge</dt><dd>{fmtDecimal(m.margin_db, 'dB')}</dd></div>
        <div><dt>Limite A</dt><dd>{fmtDecimal(m.limit_a_db, 'dB')} <span className="lc-hint">{origineLimite(m.limit_source)}</span></dd></div>
        <div><dt>LAeq instantané</dt><dd>{fmtDecimal(m.laeq_db, 'dB(A)')}</dd></div>
        <div><dt>LCeq 15 min</dt><dd>{fmtDecimal(m.lceq15_db, 'dB(C)')}{m.limit_c_db != null ? <span className="lc-hint"> / {fmtDecimal(m.limit_c_db)}</span> : null}</dd></div>
      </dl>
      <Lu age={m.data_age_s} source="Sonomètre" />
    </article>
  )
}

/* ─────────────────────────────── Lumière */

function Lumiere({ d, details }: { d: LightingDoc; details: boolean }) {
  const reseau = mesuresReseauLumiere(d.generic)
  const ages = d.nodes.flatMap((n) => n.ports.map((p) => p.data_age_s))
  const univers = reseau.find((g) => g.metric === 'lumiere.universes_active') ?? null
  // Les compteurs viennent des lignes de ports : si LumiNode ne les relit plus, ils sont gris.
  const agePorts = agePlusAncien(ages)
  const portsPerimes = d.counts.ports > 0 && mesurePerimee(agePorts)
  return (
    <>
      <Tuiles>
        <Tuile libelle="Ports DMX actifs" valeur={d.counts.ports ? fmtRatio(d.counts.active, d.counts.ports) : '—'}
               ton={d.counts.ports && !portsPerimes ? 'neutre' : 'gris'} detail={!d.counts.ports ? 'aucun port suivi' : portsPerimes ? 'donnée périmée' : 'émettent en ce moment'} source="LumiNode" age={agePorts} />
        <Tuile libelle="En maintien (Hold)" valeur={d.counts.ports ? fmtNombre(d.counts.holds) : '—'}
               ton={!d.counts.ports || portsPerimes ? 'gris' : d.counts.holds > 0 ? 'warn' : 'ok'}
               detail={portsPerimes ? 'donnée périmée' : d.counts.holds > 0 ? 'la console n’envoie plus, le nœud tient' : 'aucun port figé'} source="LumiNode" age={agePorts} />
        <Tuile libelle="Trous DMX (5 min)" valeur={fmtNombre(d.counts.gaps_5m)} ton={d.counts.gaps_5m === null || portsPerimes ? 'gris' : d.counts.gaps_5m > 0 ? 'warn' : 'ok'}
               detail={portsPerimes ? 'donnée périmée' : 'interruptions brèves du flux'} source="LumiNode" age={agePorts} />
        <Tuile libelle="Univers actifs" valeur={fmtNombre(univers?.value)} ton={univers ? 'neutre' : 'gris'} detail="Art-Net et sACN vus sur le réseau lumière" source="Écoute passive" age={univers?.data_age_s ?? null} />
      </Tuiles>

      {d.nodes.length === 0 ? (
        <Vide titre="Aucun nœud LumiNode n’est suivi par Sonde." versADecider>
          <p>Les nœuds découverts attendent d’être validés. Tant qu’ils ne le sont pas, leurs 12 ports DMX ne s’affichent pas ici.</p>
          {d.devices.length ? <p>{d.devices.length} appareil{d.devices.length > 1 ? 's' : ''} lumière au registre, sans ports DMX lus.</p> : null}
        </Vide>
      ) : (
        <div className="sv-av-noeuds">
          {d.nodes.map((n) => <Noeud key={n.device.uid} n={n} details={details} />)}
        </div>
      )}

      {reseau.length ? (
        <section className="sv-av-reseau" aria-labelledby="sv-av-reseau-titre">
          <h3 className="sv-av-sous-titre" id="sv-av-reseau-titre">Réseau lumière</h3>
          <dl className="sv-dom-faits">
            {reseau.map((g) => (
              <div key={`${g.entity_key}:${g.metric}`}>
                <dt>{g.label}{details ? <span className="sv-dom-mono"> {g.entity_key}</span> : null}</dt>
                <dd>{fmtDecimal(g.value, g.unit ?? '')} <Lu age={g.data_age_s} /></dd>
              </div>
            ))}
          </dl>
        </section>
      ) : null}
    </>
  )
}

function Noeud({ n, details }: { n: NoeudLumiere; details: boolean }) {
  const maintien = portsEnMaintien(n)
  return (
    <article className="sv-av-noeud">
      <header className="sv-av-noeud__tete">
        <span className="sv-av-noeud__nom"><LienAppareil uid={n.device.uid} nom={n.device.display_name} zone={n.device.zone_code} />{n.device.zone_code ? <span className="lc-hint"> · {nomSalle(n.device.zone_code)}</span> : null}</span>
        <PastilleEtat etat={n.device.state.display_state} libelle={n.device.state.display_label} />
      </header>
      {maintien.length ? (
        <p className="sv-av-noeud__hold">
          {maintien.length === n.ports.length ? 'Tous les ports' : `Port${maintien.length > 1 ? 's' : ''} ${maintien.join(', ')}`} en maintien : la console n’envoie plus de DMX, le nœud garde la dernière image. Le nœud, lui, fonctionne.
        </p>
      ) : null}
      <ol className="sv-av-ports" aria-label={`Ports DMX de ${n.device.display_name}`}>
        {n.ports.map((p) => {
          const e = etatPort(p)
          return (
            <li key={p.port} className={`sv-av-port sv-av-port--${e.ton}`} title={`${e.libelle}${p.max_gap_ms != null ? ` · plus long trou ${p.max_gap_ms} ms` : ''}${p.gaps_5m ? ` · ${p.gaps_5m} trous en 5 min` : ''}`}>
              <span className="sv-av-port__num">{p.port}</span>
              <span className="sv-av-port__etat">{e.libelle}</span>
              <span className="sv-av-port__meta">{p.universe != null ? `U${p.universe}` : 'U —'}{p.fps != null ? ` · ${fmtDecimal(p.fps, 'i/s', 0)}` : ''}{details && p.sources != null ? ` · ${p.sources} src` : ''}</span>
            </li>
          )
        })}
      </ol>
      <Lu age={agePlusAncien(n.ports.map((p) => p.data_age_s))} source="LumiNode" />
    </article>
  )
}
